/**
 * What the screens draw while an account write sits in the outbox (`D194`).
 *
 * A write the outbox will send is ALREADY the person's decision: the sheet
 * closed, the row moved, the toast said so. So its effect is written into the
 * Query cache the moment it is queued, and kept there until it lands — the
 * "optimistic" half of an outbox write. This file is the one table of those
 * effects, one {@link Patch} per kind, and the overlay that keeps them.
 *
 * ── WHY RE-APPLY, NOT WRITE ONCE ───────────────────────────────────────────
 * A cache edit made once is undone by the next read of the same key: a focus
 * refetch, a sibling write's invalidation, the outbox's own invalidation after
 * the FIRST of two queued plan edits. The second edit would then vanish from
 * the screen until it, too, synced — a remove that reappears, a renamed day
 * that flips back. So after every successful FETCH of a key a pending item
 * touches, every pending item that touches it is applied again, in queue
 * order ({@link wireOptimisticOverlay}). Each patch is therefore IDEMPOTENT:
 * a remove filters, an add inserts only when the line is not there, a `PUT`
 * replaces fields. A `setQueryData` is a manual update and never triggers the
 * overlay, so it cannot loop.
 *
 * ── A REFUSED WRITE TAKES ITS EFFECT WITH IT ───────────────────────────────
 * A quarantined, discarded or withdrawn item is no longer pending, so it is
 * no longer re-applied, and the overlay invalidates what it touched: the next
 * read draws the server's truth. A synced item is invalidated by the runner
 * as before.
 *
 * ── WHAT A PATCH MAY NOT DO ────────────────────────────────────────────────
 * Invent what the server computes. An added exercise's dose is the engine's
 * (`D182`), so its placeholder carries `pendingDose: true` and no target, and
 * the row says it is being prescribed (`T-156`) — never a default.
 */
import type { QueryKey } from "@repo/client/data/core";
import { exerciseKeys } from "@repo/client/data/exercise";
import { meKeys } from "@repo/client/data/me";
import { notificationKeys } from "@repo/client/data/notifications";
import { planKeys } from "@repo/client/data/plan";
import { profileKeys } from "@repo/client/data/profile";
import type { OutboxItem, OutboxState } from "@repo/client/offline";
import { reletterSessions, withSuperset } from "@repo/client/plan-edit";
import type { GetMeOutput } from "@repo/contract/iam";
import type {
  EquipmentPlace,
  ExcludedExercise,
  ExclusionListOutput,
  ExerciseGoalListOutput,
  ExerciseGoalView,
  ExerciseNoteListOutput,
  ExercisePreferenceListOutput,
  ExercisePreferenceView,
  GymListOutput,
  InjuryListInput,
  InjuryListOutput,
  MeasurementInput,
  MeasurementListOutput,
  ParqInput,
  ParqOutput,
  PlanItemView,
  PlanOutput,
  PlanSessionsReorderInput,
  PlanSessionUpdateInput,
  PlanSupersetInput,
  PlanView,
  PutExercisePreferenceInput,
} from "@repo/contract/training";
import type { Query, QueryClient } from "@tanstack/react-query";
import {
  applyEquipment,
  applyMeasurement,
} from "../features/profile/editors/cache-updates";

/** A plan line the outbox drew before the server answered. */
export type PendingPlanItemView = PlanItemView & {
  /** The engine has not prescribed it yet (`D182`, `T-156`). */
  readonly pendingDose?: true;
};

/** What a `plan.item.add`, `.replace` or `.restore` carries to draw. */
export interface PlanItemPreview {
  readonly item: PendingPlanItemView;
  /** A restore that undoes a replace: the line it takes back out. */
  readonly replacing?: string;
}

/** What a `plan.session.add` carries to draw its day (`D197`). */
export interface PlanSessionPreview {
  readonly session: PlanView["sessions"][number];
}

/** What an `exclusion.add` carries to draw its row. */
export interface ExclusionPreview {
  readonly row: ExcludedExercise;
}

/** What an `exercise-goal.add` draws until the server answers (`D200`): the
 *  goal with the best the sheet already showed, or none. */
export interface ExerciseGoalPreview {
  readonly goal: ExerciseGoalView;
}

/** What a `gym.use` draws in the equipment in effect: the gym's own list. */
export interface GymUsePreview {
  readonly equipmentIds: readonly number[];
  readonly place: EquipmentPlace | null;
}

/** What a `measurement.delete` (an undo) puts back as the latest point. */
export interface MeasurementUndoPreview {
  readonly previousLatest: MeasurementListOutput["latest"];
}

/** One cache family a kind changes, and how. */
interface Target {
  readonly prefix: QueryKey;
  readonly update: (data: unknown, item: OutboxItem) => unknown;
}

type Patch = readonly Target[];

const body = <T>(item: OutboxItem): T => item.body as T;
const preview = <T>(item: OutboxItem): T | undefined =>
  item.preview as T | undefined;

/** `{ ...cached, ...body }` — a `PUT` that echoes what it stored. */
function merge<T extends object>(prefix: QueryKey): Target {
  return {
    prefix,
    update: (data, item) =>
      data === undefined || data === null
        ? data
        : { ...(data as T), ...body<object>(item) },
  };
}

// ── The plan ─────────────────────────────────────────────────────────────────

/** `edit` over every session of a plan read, the active one and the draft. */
function overPlans(
  edit: (plan: PlanView, item: OutboxItem) => PlanView
): Target[] {
  const update = (data: unknown, item: OutboxItem) => {
    const plan = data as PlanOutput | undefined;
    return plan === undefined || plan === null ? data : edit(plan, item);
  };
  return [
    { prefix: planKeys.activeAll, update },
    { prefix: planKeys.draftAll, update: draftOnly(update) },
  ];
}

/** The draft family also holds the diff (`["plan","draft","diff",…]`),
 *  which is not a plan: only a value with sessions is edited. */
function draftOnly(
  update: (data: unknown, item: OutboxItem) => unknown
): (data: unknown, item: OutboxItem) => unknown {
  return (data, item) =>
    typeof data === "object" && data !== null && "sessions" in data
      ? update(data, item)
      : data;
}

function withSessions(
  plan: PlanView,
  change: (
    session: PlanView["sessions"][number]
  ) => PlanView["sessions"][number]
): PlanView {
  let changed = false;
  const sessions = plan.sessions.map((session) => {
    const next = change(session);
    changed ||= next !== session;
    return next;
  });
  return changed ? { ...plan, sessions } : plan;
}

/** The plan this write names — an item write carries its `planId`. */
function namesPlan(plan: PlanView, item: OutboxItem): boolean {
  const planId = (item.body as { planId?: unknown } | undefined)?.planId;
  return typeof planId !== "string" || planId === plan.planId;
}

function hasItem(plan: PlanView, itemId: string): boolean {
  return plan.sessions.some((session) =>
    session.items.some((line) => line.itemId === itemId)
  );
}

function addLine(plan: PlanView, item: OutboxItem): PlanView {
  const shown = preview<PlanItemPreview>(item)?.item;
  if (
    shown === undefined ||
    !namesPlan(plan, item) ||
    hasItem(plan, shown.itemId)
  ) {
    return plan;
  }
  return withSessions(plan, (session) => {
    if (session.sessionId !== shown.sessionId) {
      return session;
    }
    const last = session.items.at(-1);
    return {
      ...session,
      items: [
        ...session.items,
        { ...shown, position: last === undefined ? 1 : last.position + 1 },
      ],
    };
  });
}

function removeLine(plan: PlanView, itemId: string): PlanView {
  return withSessions(plan, (session) =>
    session.items.some((line) => line.itemId === itemId)
      ? {
          ...session,
          items: session.items.filter((line) => line.itemId !== itemId),
        }
      : session
  );
}

/** The new line in the old one's slot; nothing when the new one is there. */
function replaceLine(plan: PlanView, item: OutboxItem): PlanView {
  const shown = preview<PlanItemPreview>(item)?.item;
  if (
    shown === undefined ||
    !namesPlan(plan, item) ||
    hasItem(plan, shown.itemId)
  ) {
    return plan;
  }
  return withSessions(plan, (session) =>
    session.items.some((line) => line.itemId === item.entityId)
      ? {
          ...session,
          items: session.items.map((line) =>
            line.itemId === item.entityId
              ? { ...shown, position: line.position, sessionId: line.sessionId }
              : line
          ),
        }
      : session
  );
}

/**
 * A line moved to another day (`D214`): out of its day, in at the end of the
 * target one under its new id. Idempotent (`T-221`): once the new id is on
 * the plan, a second pass changes nothing; a target day that is not on this
 * plan leaves the plan alone.
 */
function moveLine(plan: PlanView, item: OutboxItem): PlanView {
  const shown = preview<PlanItemPreview>(item)?.item;
  if (
    shown === undefined ||
    !namesPlan(plan, item) ||
    hasItem(plan, shown.itemId) ||
    !plan.sessions.some((session) => session.sessionId === shown.sessionId)
  ) {
    return plan;
  }
  return withSessions(plan, (session) => {
    const kept = session.items.filter((line) => line.itemId !== item.entityId);
    if (session.sessionId !== shown.sessionId) {
      return kept.length === session.items.length
        ? session
        : { ...session, items: kept };
    }
    const last = kept.at(-1);
    return {
      ...session,
      items: [
        ...kept,
        { ...shown, position: last === undefined ? 1 : last.position + 1 },
      ],
    };
  });
}

/** The undo: the line back where it stood, the replacement (if any) out. */
function restoreLine(plan: PlanView, item: OutboxItem): PlanView {
  const shown = preview<PlanItemPreview>(item);
  if (shown === undefined || !namesPlan(plan, item)) {
    return plan;
  }
  const { replacing } = shown;
  const back = shown.item;
  if (replacing !== undefined) {
    if (!hasItem(plan, replacing)) {
      return hasItem(plan, back.itemId) ? plan : addLine(plan, item);
    }
    return withSessions(plan, (session) =>
      session.items.some((line) => line.itemId === replacing)
        ? {
            ...session,
            items: session.items.map((line) =>
              line.itemId === replacing ? back : line
            ),
          }
        : session
    );
  }
  if (hasItem(plan, back.itemId)) {
    return plan;
  }
  return withSessions(plan, (session) =>
    session.sessionId === back.sessionId
      ? {
          ...session,
          items: [...session.items, back].sort(
            (a, b) => a.position - b.position
          ),
        }
      : session
  );
}

/**
 * `D215`: a write that changes which days there are, their order or a label
 * re-letters the week, exactly as the server will store it — so a day added,
 * removed or moved offline is drawn A, B, C and not A, C, B. Only when the
 * patch changed the plan: an untouched plan is drawn as the server sent it.
 * Re-lettering is idempotent, so the overlay's re-application is safe
 * (`T-221`).
 */
function relettered(
  patch: (plan: PlanView, item: OutboxItem) => PlanView,
  applies: (item: OutboxItem) => boolean = () => true
): (plan: PlanView, item: OutboxItem) => PlanView {
  return (plan, item) => {
    const next = patch(plan, item);
    if (next === plan || !applies(item)) {
      return next;
    }
    const sessions = reletterSessions(next.sessions);
    return sessions === next.sessions
      ? next
      : { ...next, sessions: [...sessions] };
  };
}

/** The new day at the end of the plan; nothing when it is there already. */
function addDay(plan: PlanView, item: OutboxItem): PlanView {
  const shown = preview<PlanSessionPreview>(item)?.session;
  if (
    shown === undefined ||
    !namesPlan(plan, item) ||
    plan.sessions.some((session) => session.sessionId === shown.sessionId)
  ) {
    return plan;
  }
  const last = plan.sessions.at(-1);
  return {
    ...plan,
    sessions: [
      ...plan.sessions,
      { ...shown, position: last === undefined ? 1 : last.position + 1 },
    ],
  };
}

/** The day and its lines out; nothing when it is gone already. */
function removeDay(plan: PlanView, sessionId: string): PlanView {
  return plan.sessions.some((session) => session.sessionId === sessionId)
    ? {
        ...plan,
        sessions: plan.sessions.filter(
          (session) => session.sessionId !== sessionId
        ),
      }
    : plan;
}

/**
 * A superset write (`D246`) drawn onto the day its lines stand in, as the
 * server will store it (`withSuperset`). Idempotent like every patch here
 * (`T-221`): a second pass over a day already grouped returns the same day.
 * Another plan, or a day none of the lines are on, is left alone.
 */
export function groupLines(plan: PlanView, item: OutboxItem): PlanView {
  const change = body<PlanSupersetInput>(item);
  if (change.planId !== plan.planId) {
    return plan;
  }
  const named = new Set(change.itemIds);
  return withSessions(plan, (session) => {
    if (!session.items.some((line) => named.has(line.itemId))) {
      return session;
    }
    const items = withSuperset(session.items, change);
    return items === session.items
      ? session
      : { ...session, items: [...items] };
  });
}

/**
 * The days in the order the body names (`D210`), renumbered from 1. Sorting
 * by a fixed list is IDEMPOTENT — the overlay re-applies every live patch
 * after each refetch (`T-221`), and a second pass over an already-sorted plan
 * changes nothing. A day the list does not name (one added since) keeps its
 * place after the named ones; another plan is left alone.
 */
export function reorderDays(plan: PlanView, item: OutboxItem): PlanView {
  const input = body<PlanSessionsReorderInput>(item);
  if (input.planId !== plan.planId) {
    return plan;
  }
  const rank = new Map(input.sessionIds.map((id, index) => [id, index]));
  const sorted = [...plan.sessions].sort(
    (a, b) =>
      (rank.get(a.sessionId) ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(b.sessionId) ?? Number.MAX_SAFE_INTEGER) ||
      a.position - b.position
  );
  const renumbered = sorted.map((session, index) =>
    session.position === index + 1
      ? session
      : { ...session, position: index + 1 }
  );
  return renumbered.every((session, index) => session === plan.sessions[index])
    ? plan
    : { ...plan, sessions: renumbered };
}

function updateSession(plan: PlanView, item: OutboxItem): PlanView {
  const input = body<PlanSessionUpdateInput>(item);
  return withSessions(plan, (session) => {
    if (session.sessionId !== item.entityId) {
      return session;
    }
    let next = session;
    if (input.label !== undefined && input.label !== next.label) {
      next = { ...next, label: input.label };
    }
    // An EMPTY name is the server's to regenerate: the old one stays drawn
    // until the read brings the new one.
    if (
      input.name !== undefined &&
      input.name !== "" &&
      input.name !== next.name
    ) {
      next = { ...next, name: input.name };
    }
    if (input.weekday !== undefined && input.weekday !== next.weekday) {
      next = { ...next, weekday: input.weekday };
    }
    if (input.itemIds !== undefined) {
      const order = new Map(input.itemIds.map((id, index) => [id, index]));
      const sorted = [...next.items].sort(
        (a, b) =>
          (order.get(a.itemId) ?? Number.MAX_SAFE_INTEGER) -
          (order.get(b.itemId) ?? Number.MAX_SAFE_INTEGER)
      );
      if (sorted.some((line, index) => line !== next.items[index])) {
        next = {
          ...next,
          items: sorted.map((line, index) => ({
            ...line,
            position: index + 1,
          })),
        };
      }
    }
    return next;
  });
}

// ── The table ────────────────────────────────────────────────────────────────

function exclusionsTarget(
  edit: (
    rows: readonly ExcludedExercise[],
    item: OutboxItem
  ) => ExcludedExercise[] | null
): Target {
  return {
    prefix: planKeys.exclusionsAll,
    update: (data, item) => {
      const list = data as ExclusionListOutput | undefined;
      if (list === undefined) {
        return data;
      }
      const rows = edit(list.rows, item);
      return rows === null ? data : { ...list, rows };
    },
  };
}

// ── The person's own exercise data (`D200`) ─────────────────────────────────

/** The note list with this exercise's note replaced, added or removed. */
function notesTarget(
  edit: (
    notes: ExerciseNoteListOutput["notes"],
    item: OutboxItem
  ) => ExerciseNoteListOutput["notes"]
): Target {
  return {
    prefix: exerciseKeys.notes,
    update: (data, item) => {
      const list = data as ExerciseNoteListOutput | undefined;
      return list === undefined
        ? data
        : { ...list, notes: edit(list.notes, item) };
    },
  };
}

/** The preference list after a partial `PUT`: a key left out is kept, and a
 *  row that ends neutral leaves the list, as it leaves the table. */
function applyPreference(
  rows: readonly ExercisePreferenceView[],
  item: OutboxItem
): ExercisePreferenceView[] {
  const change = body<PutExercisePreferenceInput>(item);
  const held = rows.find((row) => row.exerciseId === item.entityId);
  const next: ExercisePreferenceView = {
    exerciseId: item.entityId,
    favorite: change.favorite ?? held?.favorite ?? false,
    preference:
      change.preference === undefined
        ? (held?.preference ?? null)
        : change.preference,
    updatedAt: held?.updatedAt ?? item.createdAt,
  };
  const others = rows.filter((row) => row.exerciseId !== item.entityId);
  return next.favorite || next.preference !== null
    ? [...others, next].sort((a, b) => (a.exerciseId < b.exerciseId ? -1 : 1))
    : others;
}

function goalsTarget(
  edit: (
    goals: readonly ExerciseGoalView[],
    item: OutboxItem
  ) => readonly ExerciseGoalView[] | null
): Target {
  return {
    prefix: exerciseKeys.goalsAll,
    update: (data, item) => {
      const list = data as ExerciseGoalListOutput | undefined;
      if (list === undefined) {
        return data;
      }
      const goals = edit(list.goals, item);
      return goals === null ? data : { ...list, goals: [...goals] };
    },
  };
}

const PATCHES: Partial<Record<OutboxItem["kind"], Patch>> = {
  "equipment.put": [
    {
      prefix: profileKeys.equipment,
      update: (data, item) =>
        data === undefined ? data : applyEquipment(data as never, body(item)),
    },
  ],
  "exclusion.add": [
    exclusionsTarget((rows, item) => {
      const row = preview<ExclusionPreview>(item)?.row;
      return row === undefined ||
        rows.some((one) => one.exerciseId === row.exerciseId)
        ? null
        : [row, ...rows];
    }),
  ],
  "exclusion.remove": [
    exclusionsTarget((rows, item) =>
      rows.some((one) => one.exerciseId === item.entityId)
        ? rows.filter((one) => one.exerciseId !== item.entityId)
        : null
    ),
  ],
  "exercise-goal.add": [
    goalsTarget((goals, item) => {
      const shown = preview<ExerciseGoalPreview>(item)?.goal;
      return shown === undefined ||
        goals.some((goal) => goal.goalId === shown.goalId)
        ? null
        : [shown, ...goals];
    }),
  ],
  "exercise-goal.remove": [
    goalsTarget((goals, item) =>
      goals.some((goal) => goal.goalId === item.entityId)
        ? goals.filter((goal) => goal.goalId !== item.entityId)
        : null
    ),
  ],
  "exercise-note.put": [
    notesTarget((notes, item) => [
      ...notes.filter((note) => note.exerciseId !== item.entityId),
      {
        body: body<{ body: string }>(item).body.trim(),
        exerciseId: item.entityId,
        updatedAt: item.createdAt,
      },
    ]),
  ],
  "exercise-note.remove": [
    notesTarget((notes, item) =>
      notes.filter((note) => note.exerciseId !== item.entityId)
    ),
  ],
  "exercise-preference.put": [
    {
      prefix: exerciseKeys.preferences,
      update: (data, item) => {
        const list = data as ExercisePreferenceListOutput | undefined;
        return list === undefined
          ? data
          : { ...list, preferences: applyPreference(list.preferences, item) };
      },
    },
  ],
  // `D202`: the chip moves at the tap, and so does the equipment in effect —
  // both are exactly what the server will answer (`gyms.ts` writes the gym's
  // list, its place when it has one), so nothing here is a guess.
  "gym.use": [
    {
      prefix: profileKeys.gyms,
      update: (data, item) => {
        const list = data as GymListOutput | undefined;
        if (list === undefined) {
          return data;
        }
        const { gymId } = body<{ gymId: string }>(item);
        return {
          ...list,
          currentGymId: gymId,
          gyms: list.gyms.map((gym) =>
            gym.id === gymId ? { ...gym, lastUsedAt: item.createdAt } : gym
          ),
        };
      },
    },
    {
      prefix: profileKeys.equipment,
      update: (data, item) => {
        const drawn = preview<GymUsePreview>(item);
        return data === undefined || drawn === undefined
          ? data
          : applyEquipment(data as never, {
              available: [...drawn.equipmentIds],
              ...(drawn.place === null ? {} : { place: drawn.place }),
            });
      },
    },
  ],
  "injuries.put": [
    {
      prefix: profileKeys.injuries,
      update: (data, item) =>
        data === undefined
          ? data
          : {
              ...(data as InjuryListOutput),
              injuries: body<InjuryListInput>(item).injuries,
            },
    },
  ],
  "me.display-name.put": [
    {
      prefix: meKeys.account,
      update: (data, item) =>
        data === undefined
          ? data
          : {
              ...(data as GetMeOutput),
              displayName: body<{ displayName: string | null }>(item)
                .displayName,
            },
    },
  ],
  // `D266`. Idempotent (`T-221`): it sets the field, so drawing it twice
  // draws the same account.
  "me.weight-unit.put": [
    {
      prefix: meKeys.account,
      update: (data, item) =>
        data === undefined
          ? data
          : {
              ...(data as GetMeOutput),
              weightUnit: body<{ weightUnit: GetMeOutput["weightUnit"] }>(item)
                .weightUnit,
            },
    },
  ],
  "me.locale.put": [
    {
      prefix: meKeys.account,
      update: (data, item) =>
        data === undefined
          ? data
          : {
              ...(data as GetMeOutput),
              locale: body<{ locale: GetMeOutput["locale"] }>(item).locale,
            },
    },
  ],
  "measurement.add": [
    {
      prefix: profileKeys.measurementsAll,
      update: (data, item) =>
        data === undefined
          ? data
          : applyMeasurement(
              data as MeasurementListOutput,
              body<MeasurementInput>(item)
            ),
    },
  ],
  "measurement.delete": [
    {
      prefix: profileKeys.measurementsAll,
      update: (data, item) => {
        const list = data as MeasurementListOutput | undefined;
        const undone = preview<MeasurementUndoPreview>(item);
        if (list === undefined || undone === undefined) {
          return data;
        }
        const { latest } = list;
        const pendingId = `pending:${item.entityId}`;
        const ownId = (item.body as { measurementId?: string } | undefined)
          ?.measurementId;
        return latest !== null &&
          (latest.measurementId === pendingId || latest.measurementId === ownId)
          ? { ...list, latest: undone.previousLatest }
          : data;
      },
    },
  ],
  "notification-prefs.put": [merge(notificationKeys.preferences)],
  "parq.put": [
    {
      prefix: profileKeys.parq,
      update: (data, item) => {
        if (data === undefined) {
          return data;
        }
        const { answers } = body<ParqInput>(item);
        return {
          ...(data as ParqOutput),
          answers,
          positiveCount: answers.filter(Boolean).length,
        };
      },
    },
  ],
  "plan.item.add": overPlans(addLine),
  "plan.item.remove": overPlans((plan, item) =>
    removeLine(plan, item.entityId)
  ),
  "plan.item.replace": overPlans(replaceLine),
  "plan.item.restore": overPlans(restoreLine),
  // The same drawing as a replace: the new line in the old one's slot.
  "plan.item.retarget": overPlans(replaceLine),
  "plan.session.add": overPlans(relettered(addDay)),
  "plan.session.remove": overPlans(
    relettered((plan, item) => removeDay(plan, item.entityId))
  ),
  "plan.session.update": overPlans(
    relettered(
      updateSession,
      (item) => body<PlanSessionUpdateInput>(item).label !== undefined
    )
  ),
  "plan.sessions.reorder": overPlans(relettered(reorderDays)),
  "plan.item.move": overPlans(moveLine),
  "plan.superset": overPlans(groupLines),
  "plan.suggestion.dismiss": [
    {
      prefix: planKeys.activeAll,
      update: (data) => {
        const plan = data as PlanOutput | undefined;
        return plan === undefined ||
          plan === null ||
          plan.suggestion === null ||
          plan.suggestion === undefined
          ? data
          : { ...plan, suggestion: null };
      },
    },
  ],
  "profile.put": [merge(profileKeys.profile)],
  "training-config.put": [merge(profileKeys.trainingConfig)],
};

function startsWith(key: readonly unknown[], prefix: QueryKey): boolean {
  return prefix.every((part, index) => key[index] === part);
}

function isLive(item: OutboxItem): boolean {
  return item.state === "pending" || item.state === "in_flight";
}

/** Apply ONE item's effect to every cached query it touches. */
export function applyOptimistic(client: QueryClient, item: OutboxItem): void {
  for (const target of PATCHES[item.kind] ?? []) {
    for (const [key, data] of client.getQueriesData({
      queryKey: target.prefix,
    })) {
      const next = target.update(data, item);
      if (next !== data) {
        client.setQueryData(key, next);
      }
    }
  }
}

/** Re-apply every live item that touches `key`, in queue order. */
export function reapplyFor(
  client: QueryClient,
  state: OutboxState,
  key: readonly unknown[]
): void {
  for (const item of state.items) {
    if (!isLive(item)) {
      continue;
    }
    for (const target of PATCHES[item.kind] ?? []) {
      if (!startsWith(key, target.prefix)) {
        continue;
      }
      const data = client.getQueryData(key);
      const next = target.update(data, item);
      if (next !== data) {
        client.setQueryData(key, next);
      }
    }
  }
}

/** The prefixes a kind's effect lives under — what to invalidate when the
 *  write is refused or withdrawn and its effect must go. */
export function touchedBy(kind: OutboxItem["kind"]): readonly QueryKey[] {
  return (PATCHES[kind] ?? []).map((target) => target.prefix);
}

export interface OverlayStore {
  getState(): OutboxState;
  subscribe(listener: (state: OutboxState) => void): () => void;
}

/**
 * Keep the queued effects on screen (header). Returns the teardown.
 *
 * - A successful, non-manual FETCH of a query is followed by the live items
 *   that touch its key, re-applied.
 * - An item that stops being live without being sent — quarantined,
 *   discarded, withdrawn — has its keys invalidated, so the server's truth
 *   comes back. One that was sent is invalidated by the runner already; the
 *   second invalidation of the same keys is deduplicated by TanStack.
 * - An item that BECOMES live is applied once: the queue's first load after
 *   a launch, whose effects a cache restored from before them (or busted
 *   since) does not hold. A new write was applied by `useOutboxWrite`
 *   already, and applying it again changes nothing.
 */
export function wireOptimisticOverlay(
  client: QueryClient,
  store: OverlayStore
): () => void {
  const offCache = client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success") {
      return;
    }
    if ((event.action as { manual?: boolean }).manual === true) {
      return;
    }
    reapplyFor(client, store.getState(), (event.query as Query).queryKey);
  });
  let live = new Map(
    store
      .getState()
      .items.filter(isLive)
      .map((item) => [item.id, item.kind] as const)
  );
  const offStore = store.subscribe((state) => {
    const now = new Map(
      state.items.filter(isLive).map((item) => [item.id, item.kind] as const)
    );
    for (const item of state.items) {
      if (isLive(item) && !live.has(item.id)) {
        applyOptimistic(client, item);
      }
    }
    const gone = new Set<string>();
    for (const [id, kind] of live) {
      if (!now.has(id)) {
        for (const prefix of touchedBy(kind)) {
          gone.add(JSON.stringify(prefix));
        }
      }
    }
    live = now;
    for (const prefix of gone) {
      client
        .invalidateQueries({ queryKey: JSON.parse(prefix) as QueryKey })
        .catch((error: unknown) => {
          console.warn("[data] invalidating a refused write failed", error);
        });
    }
  });
  return () => {
    offCache();
    offStore();
  };
}
